// apps/mtd-itsa-web/src/pages/obligations.tsx

import { useCallback, useEffect, useMemo, useState, type Key } from 'react';
import {
    AlertCircle,
    Calendar,
    File04,
    Plus,
    RefreshCw01,
    ShieldTick,
} from '@untitledui/icons';

import { HmrcConnectDialog, type HmrcUserType } from '@/components/application/hmrc/hmrc-connect-dialog';
import ObligationsTable, {
    type FinalObligation,
    type HmrcBusinessSummary,
    type QuarterlyObligation,
} from '@/components/application/table/obligations-table';
import { Button } from '@/components/base/buttons/button';
import { Select } from '@/components/base/select/select';
import { useHandleError } from '@/hooks/use-handle-error';
import { useAuth } from '@/providers/auth-provider';
import { useProject } from '@/providers/project-provider';
import { getSupabaseClient } from '@kordbooks/supabase-client';
import { useToast } from "@/components/application/toast";
import { getTestScenarioHeader } from '@/utils/test-headers';
import { useFraudContext } from '@/hooks/use-fraud-context';



type TokenStatus = 'checking' | 'valid' | 'expired' | 'none';



// Generate options: Current year + 1 ahead + 2 previous
function getTaxYearOptions() {
    const currentYear = new Date().getFullYear();
    const startYear = 2018;
    const endYear = currentYear + 1;
    const years = [];
    for (let year = startYear; year <= endYear; year++) {
        years.push(year);
    }
    return years;
}

export default function Obligations() {
    const { user } = useAuth();
    const { currentProjectId } = useProject();
    const handleError = useHandleError();
    const { toast } = useToast();
    const fraudContext = useFraudContext();

    const [isConnecting, setIsConnecting] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isLoadingFromDb, setIsLoadingFromDb] = useState(false);
    const [tokenStatus, setTokenStatus] = useState<TokenStatus>('checking');

    // Default to the tax year relevant to "today"
    const [selectedStartYear, setSelectedStartYear] = useState(() => {
        const now = new Date();
        const isBeforeApril6 = now.getMonth() < 3 || (now.getMonth() === 3 && now.getDate() < 6);
        return isBeforeApril6 ? now.getFullYear() - 1 : now.getFullYear();
    });

    // Raw data from DB (contains all years)
    const [businesses, setBusinesses] = useState<HmrcBusinessSummary[]>([]);
    const [quarterlyObligations, setQuarterlyObligations] = useState<QuarterlyObligation[]>([]);
    const [finalObligations, setFinalObligations] = useState<FinalObligation[]>([]);

    const apiUrl = useMemo(() => import.meta.env.VITE_API_URL as string | undefined, []);

    // Filter Quarterly Obligations by Selected Tax Year
    const visibleQuarterly = useMemo(() => {
        if (!quarterlyObligations.length) return [];
        const start = new Date(`${selectedStartYear}-04-06`);
        const end = new Date(`${selectedStartYear + 1}-04-05`);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        return quarterlyObligations.filter((o) => {
            if (!o.period_start_date) return false;
            const d = new Date(o.period_start_date);
            return d >= start && d <= end;
        });
    }, [quarterlyObligations, selectedStartYear]);

    // Filter Final Obligations by Selected Tax Year
    const visibleFinal = useMemo(() => {
        if (!finalObligations.length) return [];
        const start = new Date(`${selectedStartYear}-04-06`);
        const end = new Date(`${selectedStartYear + 1}-04-05`);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        return finalObligations.filter((o) => {
            if (!o.period_start_date) return false;
            const d = new Date(o.period_start_date);
            return d >= start && d <= end;
        });
    }, [finalObligations, selectedStartYear]);

    const fetchAllObligationsFromDb = useCallback(async () => {
        if (!user?.id || !currentProjectId) {
            setBusinesses([]);
            setQuarterlyObligations([]);
            setFinalObligations([]);
            return;
        }

        setIsLoadingFromDb(true);
        try {
            const supabase = getSupabaseClient();

            // 1) Businesses
            const { data: bizData, error: bizError } = await supabase
                .schema('mtd_itsa')
                .from('hmrc_businesses')
                .select('business_id, trading_name, type_of_business')
                .eq('project_id', currentProjectId)
                .order('created_at', { ascending: true });

            if (bizError) {
                handleError(bizError, 'Failed to load HMRC businesses');
                setBusinesses([]);
                setQuarterlyObligations([]);
                setFinalObligations([]);
                return;
            }
            setBusinesses((bizData ?? []) as HmrcBusinessSummary[]);

            // 2) Quarterly
            const { data: qData, error: qError } = await supabase
                .schema('mtd_itsa')
                .from('hmrc_obligations_quarterly')
                .select(
                    'project_id, business_id, period_start_date, period_end_date, due_date, status, received_date, type_of_business, is_deleted',
                )
                .eq('project_id', currentProjectId);

            if (qError) handleError(qError, 'Failed to load quarterly obligations');
            else setQuarterlyObligations((qData ?? []) as QuarterlyObligation[]);

            // 3) Final
            const { data: fData, error: fError } = await supabase
                .schema('mtd_itsa')
                .from('hmrc_obligations_final')
                .select(
                    'project_id, period_start_date, period_end_date, due_date, status, received_date, is_deleted',
                )
                .eq('project_id', currentProjectId);

            if (fError) handleError(fError, 'Failed to load final obligations');
            else setFinalObligations((fData ?? []) as FinalObligation[]);
        } catch (err) {
            handleError(err, 'Unexpected error while loading obligations');
        } finally {
            setIsLoadingFromDb(false);
        }
    }, [currentProjectId, handleError, user?.id]);

    useEffect(() => {
        const checkTokenStatus = async () => {
            if (!user?.id || !currentProjectId) {
                setTokenStatus('none');
                return;
            }

            try {
                const supabase = getSupabaseClient();
                const { data, error } = await supabase
                    .schema('mtd_itsa')
                    .from('hmrc_user_tokens')
                    // we only need to know if a row exists; access tokens expiring is expected
                    .select('token_id')
                    .eq('user_uuid', user.id)
                    .eq('project_id', currentProjectId)
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .maybeSingle();

                if (error) {
                    handleError(error, 'Failed to check HMRC token status');
                    setTokenStatus('none');
                    return;
                }

                setTokenStatus(data?.token_id ? 'valid' : 'none');
            } catch (err) {
                handleError(err, 'Unexpected error while checking token');
                setTokenStatus('none');
            }
        };

        checkTokenStatus();
    }, [currentProjectId, handleError, user?.id]);

    useEffect(() => {
        if (tokenStatus !== 'valid') {
            setBusinesses([]);
            setQuarterlyObligations([]);
            setFinalObligations([]);
            return;
        }
        fetchAllObligationsFromDb();
    }, [fetchAllObligationsFromDb, tokenStatus]);


    const syncWithHmrc = useCallback(async () => {
        if (!user?.id || !currentProjectId) {
            toast({
                title: 'Cannot sync',
                description: 'Missing user or project.',
                variant: 'error',
            });
            handleError('Missing user or project', 'Sync with HMRC');
            return;
        }

        if (!apiUrl) {
            toast({
                title: 'Cannot sync',
                description: 'API URL is not configured.',
                variant: 'error',
            });
            handleError('API URL is not configured', 'Sync with HMRC');
            return;
        }

        // 2. Sync Obligations for the selected tax year (compute up-front for messaging too)
        const fromDate = `${selectedStartYear}-04-06`;
        const toDate = `${selectedStartYear + 1}-04-05`;
        const taxYearParam = `${selectedStartYear}-${(selectedStartYear + 1).toString().slice(-2)}`;

        setIsSyncing(true);

        try {
            const supabase = getSupabaseClient();
            const { data: meta, error: metaError } = await supabase
                .schema('mtd_itsa')
                .from('hmrc_projects_meta')
                .select('nino')
                .eq('project_id', currentProjectId)
                .maybeSingle();

            if (metaError) throw metaError;

            const nino = meta?.nino?.trim();
            if (!nino) {
                throw new Error(
                    'No NINO found for this project. Connect/setup HMRC for this project first.',
                );
            }

            const headers = {
                'Content-Type': 'application/json',
                'x-user-uuid': user.id,
                'x-project-id': currentProjectId,
                'x-fraud-context': JSON.stringify(fraudContext),
                ...getTestScenarioHeader(),
            };

            // 1) Sync Businesses
            const bizRes = await fetch(`${apiUrl}/api/hmrc/business-details/list`, {
                method: 'GET',
                headers,
            });

            if (!bizRes.ok && bizRes.status !== 204) {
                const payload = await bizRes.json().catch(() => null);
                throw new Error(payload?.message || `Failed to refresh businesses (${bizRes.status})`);
            }

            // 2) Sync Obligations
            const quarterlyPromise = fetch(
                `${apiUrl}/api/hmrc/obligations/income-and-expenditure?fromDate=${encodeURIComponent(
                    fromDate,
                )}&toDate=${encodeURIComponent(toDate)}`,
                { method: 'GET', headers },
            );

            const finalPromise = fetch(
                `${apiUrl}/api/hmrc/obligations/crystallisation?taxYear=${encodeURIComponent(
                    taxYearParam,
                )}`,
                { method: 'GET', headers },
            );

            const [qRes, fRes] = await Promise.all([quarterlyPromise, finalPromise]);

            if (qRes.status === 429 || fRes.status === 429) {
                // Refresh UI to show updated businesses (Step 1 was successful)
                await fetchAllObligationsFromDb();

                toast({
                    title: 'Synced recently',
                    description: 'Please try again in a 10 minutes.',
                    variant: 'warning',
                });
                // Exit early to avoid showing "Sync complete" or generic failure messages
                return;
            }

            let quarterlyOk = true;
            let finalOk = true;

            if (!qRes.ok) {
                quarterlyOk = false;
                console.warn('Quarterly sync failed', await qRes.json().catch(() => null));
            }
            if (!fRes.ok) {
                finalOk = false;
                console.warn('Final sync failed', await fRes.json().catch(() => null));
            }

            // 3) Refresh UI
            await fetchAllObligationsFromDb();

            if (quarterlyOk && finalOk) {
                toast({
                    title: 'Sync complete',
                    description: `HMRC businesses and obligations updated for ${selectedStartYear}–${selectedStartYear + 1}.`,
                    variant: 'success',
                });
            } else {
                const errorMessages = [
                    !quarterlyOk ? 'Quarterly obligations sync failed.' : null,
                    !finalOk ? 'Final (crystallisation) obligations sync failed.' : null,
                ].filter(Boolean).join(' ');

                toast({
                    title: 'Obligations sync failed',
                    description: errorMessages,
                    variant: 'error',
                });
            }
        } catch (err) {
            toast({
                title: 'Sync failed',
                description: err instanceof Error ? err.message : 'Unexpected error while syncing.',
                variant: 'error',
            });
            handleError(err, 'Failed to sync with HMRC');
        } finally {
            setIsSyncing(false);
        }
    }, [
        apiUrl,
        currentProjectId,
        selectedStartYear,
        fetchAllObligationsFromDb,
        handleError,
        toast,
        user?.id,
    ]);


    const handleUserTypeConnect = async (userType: HmrcUserType, projectId: string) => {
        if (!user?.id) {
            handleError('User not authenticated', 'HMRC Connection');
            return;
        }

        try {
            setIsConnecting(true);
            setTokenStatus('checking');

            if (!apiUrl) throw new Error('API URL is not configured');

            const response = await fetch(
                `${apiUrl}/api/hmrc/oauth/connect?user_uuid=${encodeURIComponent(
                    user.id,
                )}&usertype=${encodeURIComponent(userType)}&project_id=${encodeURIComponent(projectId)}`,
                {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                },
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                    errorData.message || `Failed to initiate HMRC connection: ${response.status}`,
                );
            }

            const data = await response.json();
            if (!data?.authUrl) {
                throw new Error('No authorization URL returned from server');
            }

            window.location.href = data.authUrl;
        } catch (error) {
            handleError(error, 'Failed to connect to HMRC');
            setTokenStatus(tokenStatus === 'expired' ? 'expired' : 'none');
        } finally {
            setIsConnecting(false);
        }
    };

    return (
        <div className="flex flex-col gap-8">
            {/* Page Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-display-sm font-semibold text-primary md:text-display-md">
                        Obligations
                    </h1>
                    <p className="mt-2 text-sm text-tertiary">
                        View upcoming and past MTD reporting obligations.
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    {tokenStatus === 'valid' && (
                        <>
                            <div className="w-40">
                                <Select
                                    size="sm"
                                    placeholderIcon={Calendar}
                                    aria-label="Tax year"
                                    // New API: Use 'value' instead of 'selectedKey'
                                    value={selectedStartYear.toString()}
                                    // New API: Use 'onChange' instead of 'onSelectionChange'
                                    onChange={(key: Key) => {
                                        setSelectedStartYear(Number(key));
                                    }}
                                >
                                    {getTaxYearOptions().map((year) => {
                                        const label = `${year}/${(year + 1).toString().slice(-2)}`;
                                        return (
                                            <Select.Item
                                                key={year.toString()}
                                                id={year.toString()}
                                                textValue={label}
                                            >
                                                {label}
                                            </Select.Item>
                                        );
                                    })}
                                </Select>
                            </div>

                            <Button
                                size="sm"
                                color="secondary"
                                iconLeading={RefreshCw01}
                                disabled={isSyncing || isConnecting}
                                onClick={syncWithHmrc}
                            >
                                {isSyncing ? 'Syncing...' : 'Sync Year'}
                            </Button>
                        </>
                    )}

                    {tokenStatus !== 'valid' && (
                        <HmrcConnectDialog
                            isConnecting={isConnecting}
                            onConnect={handleUserTypeConnect}
                            trigger={
                                <Button
                                    size="sm"
                                    color="primary"
                                    iconLeading={Plus}
                                    disabled={isConnecting}
                                    aria-label="Add HMRC Account"
                                >
                                    {(tokenStatus === 'checking' ||
                                        tokenStatus === 'expired' ||
                                        tokenStatus === 'none') &&
                                        !isConnecting
                                        ? 'Add HMRC Account'
                                        : isConnecting
                                            ? 'Connecting...'
                                            : 'Button'}
                                </Button>
                            }
                        />
                    )}

                    {tokenStatus === 'valid' && (
                        <div className="flex items-center gap-2 rounded-full border border-secondary bg-secondary px-3 py-1">
                            <ShieldTick className="h-4 w-4 text-success-primary" />
                            <span className="text-xs font-medium text-secondary">Connected</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Content Area */}
            <div className="flex flex-col gap-6">
                <div className="flex flex-col items-center justify-center rounded-xl border border-secondary bg-primary py-12 text-center shadow-sm">
                    <div
                        className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full ${tokenStatus === 'valid'
                            ? 'bg-success-primary/10'
                            : tokenStatus === 'expired'
                                ? 'bg-error-primary/10'
                                : 'bg-secondary'
                            }`}
                    >
                        {tokenStatus === 'valid' ? (
                            <ShieldTick className="h-6 w-6 text-success-primary" />
                        ) : tokenStatus === 'expired' ? (
                            <AlertCircle className="h-6 w-6 text-error-primary" />
                        ) : (
                            <File04 className="h-6 w-6 text-tertiary" />
                        )}
                    </div>

                    <h3 className="text-lg font-semibold text-primary">
                        {tokenStatus === 'checking'
                            ? 'Checking connection...'
                            : tokenStatus === 'valid'
                                ? `Viewing Tax Year ${selectedStartYear}–${selectedStartYear + 1}`
                                : tokenStatus === 'expired'
                                    ? 'Connection Expired'
                                    : 'No obligations found'}
                    </h3>

                    <p className="mt-1 max-w-sm text-sm text-tertiary">
                        {tokenStatus === 'checking'
                            ? 'Please wait while we verify your account status.'
                            : tokenStatus === 'valid'
                                ? 'Your account is connected. You can sync to refresh your HMRC businesses and obligations.'
                                : tokenStatus === 'expired'
                                    ? 'Your HMRC connection has expired. Please reconnect to view your obligations.'
                                    : 'Connect your HMRC account to view your upcoming tax submission deadlines.'}
                    </p>

                    {/* Action Buttons */}
                    <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row">
                        <Button
                            size="sm"
                            color="secondary"
                            disabled={tokenStatus !== 'valid' || isSyncing || isConnecting}
                            onClick={tokenStatus === 'valid' ? syncWithHmrc : undefined}
                        >
                            {isSyncing ? 'Syncing...' : 'Sync with HMRC'}
                        </Button>

                        <HmrcConnectDialog
                            isConnecting={isConnecting}
                            onConnect={handleUserTypeConnect}
                            trigger={
                                <Button
                                    size="sm"
                                    color={tokenStatus === 'expired' ? 'primary' : 'secondary'}
                                    disabled={isConnecting}
                                >
                                    {tokenStatus === 'checking' || tokenStatus === 'expired'
                                        ? 'Reconnect Account'
                                        : 'Connect Account'}
                                </Button>
                            }
                        />
                    </div>
                </div>

                {/* Filtered Obligations Table */}
                {tokenStatus === 'valid' && (
                    <div className={isLoadingFromDb ? 'pointer-events-none opacity-50' : ''}>
                        {isLoadingFromDb && (
                            <div className="mb-4 rounded-lg bg-secondary/30 p-4 text-center text-tertiary">
                                Loading obligations...
                            </div>
                        )}
                        <ObligationsTable
                            businesses={businesses}
                            quarterlyObligations={visibleQuarterly}
                            finalObligations={visibleFinal}
                            hideDeleted
                        />
                    </div>
                )}
            </div>
        </div>
    );
}


